import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import {
  TypeFilterDate,
  TypeFilterType,
  TypeInitialState,
  TypeLoading,
  TypeParamsRequest,
  TypeRestaurant,
} from '@pages/OwnerPersonalPage/model/OwnerReviewsModel/types'
import {
  TypeMappingPaginationReviewOutput,
  TypeMappingReviewOutput,
} from '@shared/api/middleware/mappingAPI/review/type'

const initialState: TypeInitialState = {
  is_ready: false,
  loading: {
    full: true,
    addition: false,
  },
  restaurant: {
    id: null,
    name: null,
  },
  pagination: {
    total: undefined,
    page: {
      count: 0,
      size: 0,
      next: {
        link: '',
        number: 0,
      },
      previous: {
        link: '',
        number: 0,
      },
      current: {
        number: 0,
      },
    },
    positive: 0,
    negative: 0,
  },
  filters: {
    types: [
      {
        id: 1,
        type: 'all',
        title: 'Все',
        count: 0,
        visible: true,
        select: true,
        paramsRequest: {
          is_positive_like: undefined,
          owner_comment_created_isnull: undefined,
        },
      },
      {
        id: 2,
        type: 'unanswered',
        title: 'Без ответа',
        count: 0,
        visible: true,
        select: false,
        paramsRequest: {
          is_positive_like: undefined,
          owner_comment_created_isnull: true,
        },
      },
      {
        id: 3,
        type: 'positive',
        title: 'Положительные',
        count: 0,
        visible: true,
        select: false,
        paramsRequest: {
          is_positive_like: true,
          owner_comment_created_isnull: undefined,
        },
      },
      {
        id: 4,
        type: 'negative',
        title: 'Отрицательные',
        count: 0,
        visible: true,
        select: false,
        paramsRequest: {
          is_positive_like: false,
          owner_comment_created_isnull: undefined,
        },
      },
    ],
  },

  paramsRequest: {},
  reviewsByFilters: [],
  components: {
    create: {
      id_review: null,
      loading: false,
      body: null,
    },
    delete: {
      id_review: null,
      loading: false,
    },
    edit: {
      id_review: null,
      loading: false,
      body: null,
    },
  },
}

const OwnerReviews = createSlice({
  name: 'OwnerReviews',
  initialState,
  reducers: {
    setLoading(state, action: PayloadAction<TypeLoading>) {
      state.loading = action.payload
    },

    // Виджет готов к отображению данных
    setReadyWidget(state, action: PayloadAction<boolean>) {
      state.is_ready = action.payload
    },

    setRestaurant(state, action: PayloadAction<TypeRestaurant>) {
      state.restaurant = action.payload
    },

    setPagination(state, action: PayloadAction<TypeMappingPaginationReviewOutput>) {
      state.pagination = action.payload
    },

    setPaginationItems(state, action: PayloadAction<number | null>) {
      state.pagination.total = action.payload ?? undefined
    },

    setPaginationPage(state, action: PayloadAction<TypeMappingPaginationReviewOutput>) {
      state.pagination.page = action.payload.page
    },

    setParamsRequest(state, action: PayloadAction<TypeParamsRequest | null>) {
      state.paramsRequest = action.payload || {}
    },

    setListReviewsAll(state, action: PayloadAction<TypeMappingReviewOutput[]>) {
      state.reviewsByFilters = action.payload
    },

    addListReviewsAll(state, action: PayloadAction<TypeMappingReviewOutput[]>) {
      state.reviewsByFilters = [...state.reviewsByFilters, ...action.payload]
    },

    initFilterRates(
      state,
      action: PayloadAction<{
        all?: number
        positive?: number
        negative?: number
        unanswered?: number
      }>,
    ) {
      state.filters.types = state.filters.types.map((filter) => ({
        ...filter,
        select: filter.type === 'all',
        count: action.payload[filter.type] ?? 0,
        visible: !!action.payload[filter.type],
      }))
    },

    changeFilter(state, action: PayloadAction<{ type: 'types' | 'dates'; data: TypeFilterType | TypeFilterDate }>) {
      const { type, data } = action.payload
      const filters = state.filters[type]
      if (!filters) return

      state.filters[type] = filters.map((filter) => ({ ...filter, select: data.id === filter.id }))
    },

    changeComponentCreate(state, action: PayloadAction<{ body?: string | null; loading?: boolean }>) {
      state.components.create = { ...state.components.create, ...action.payload }
    },

    changeComponentEdit(state, action: PayloadAction<{ body?: string | null; loading?: boolean }>) {
      state.components.edit = { ...state.components.edit, ...action.payload }
    },

    changeComponentDelete(state, action: PayloadAction<{ loading?: boolean }>) {
      state.components.delete = { ...state.components.delete, ...action.payload }
    },

    // RESET
    resetStateReviews() {
      return initialState
    },
  },
})

export const {
  setReadyWidget,
  setLoading,
  setRestaurant,
  setPagination,
  setPaginationItems,
  setPaginationPage,
  setParamsRequest,
  setListReviewsAll,
  addListReviewsAll,

  // filters
  initFilterRates,
  changeFilter,

  // components
  changeComponentCreate,
  changeComponentEdit,
  changeComponentDelete,

  // reset
  resetStateReviews,
} = OwnerReviews.actions

export default OwnerReviews.reducer
